import React, { Dispatch, SetStateAction, useContext, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import ContentGuard from 'src/layout/ContentGuard/ContentGuard';
import { UserSessionContext } from 'src/context/UserSessionContext';
import Drawer from '@mui/material/Drawer';
import User from 'src/models/user';
import * as globalConstants from 'src/constants/index';
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import MailIcon from '@mui/icons-material/Mail';
import { useDesktopMediaQuery } from 'src/hooks/useDesktopMediaQuery';

import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import EmailIcon from '@mui/icons-material/Email';
import BrushIcon from '@mui/icons-material/Brush';
import AppsIcon from '@mui/icons-material/Apps';
import LogoutIcon from '@mui/icons-material/Logout';
import { FadeInSection } from 'src/components/FadeInSection/FadeInSection';

const Navigation: React.FC<{
  menuDrawerOpen: boolean;
  setMenuDrawerOpen: Dispatch<SetStateAction<boolean>>;
}> = ({ menuDrawerOpen, setMenuDrawerOpen }) => {
  const isDesktopScreenSize = useDesktopMediaQuery();
  const { user, setUser } = useContext(UserSessionContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const handleLogout = () => {
    setUser({} as User);
    navigate('/login');
  };
  const mainMenuItems = [
    {
      name: 'About Me',
      // icon: <EmojiPeopleIcon />,
      userAccessAllowed:
        typeof user.roles != 'undefined' && user.roles.includes('user'),
      action: () => {
        navigate('/home#about-me');
      },
    },
    {
      name: 'Experience',
      // icon: <EmojiPeopleIcon />,
      userAccessAllowed:
        typeof user.roles != 'undefined' && user.roles.includes('user'),
      action: () => {
        navigate('/home#experience');
      },
    },
    {
      name: 'Projects',
      // icon: <EmojiPeopleIcon />,
      userAccessAllowed:
        typeof user.roles != 'undefined' && user.roles.includes('user'),
      action: () => {
        navigate('/home#projects');
      },
    },
    {
      name: 'Contact Me',
      // icon: <EmailIcon />,
      userAccessAllowed:
        typeof user.roles != 'undefined' && user.roles.includes('user'),
      action: () => {
        navigate('/home#contact-me');
      },
    },
    {
      name: 'Mini Apps',
      // icon: <AppsIcon />,
      userAccessAllowed:
        typeof user.roles != 'undefined' && user.roles.includes('user'),
      action: () => {
        navigate('/mini-apps');
      },
    },
    {
      name: 'Sign Out',
      icon: <LogoutIcon />,
      userAccessAllowed: typeof user?.id !== 'undefined',
      action: handleLogout,
    },
  ];

  const secretMenuItems = [
    {
      name: 'Design Pages',
      icon: <></>,
      userAccessAllowed:
        typeof user.roles != 'undefined' &&
        user.roles.includes('user') &&
        user.name.endsWith('77777'),
      action: () => {
        navigate('/design');
      },
    },
  ];

  const anySecretMenuItemsAllowed = () => {
    return secretMenuItems.reduce((anyItemAllowed, menuItem) => {
      return anyItemAllowed || menuItem.userAccessAllowed;
    }, false);
  }

  return (
    <>
      <Drawer
        PaperProps={{ sx: { borderRadius: '0' } }}
        anchor={'right'}
        open={menuDrawerOpen}
        onClose={() => {
          setMenuDrawerOpen(false);
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: '128px 0 0 0',
            width: '100vw',
            height: '100vh',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '32px',
              width: '80%',
            }}
          >
            {mainMenuItems.map((menuItem, index) => (
              <ContentGuard
                key={menuItem.name}
                contentType={globalConstants.CONTENT_GUARD_REACT_ELEMENT}
                // a 'user' that has 'elevated' privileges
                isAllowed={menuItem.userAccessAllowed}
              >
                <FadeInSection delay={(index + 1) * 100}>
                  <Box
                    key={menuItem.name}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Button
                      fullWidth
                      onClick={() => {
                        setMenuDrawerOpen(false);
                        menuItem.action();
                      }}
                    >
                      <IconButton>{menuItem?.icon}</IconButton>
                      <Typography
                        sx={{
                          fontSize: theme.typography.h4,
                        }}
                      >
                        {menuItem.name}
                      </Typography>
                    </Button>
                  </Box>
                </FadeInSection>
              </ContentGuard>
            ))}
          </Box>
          {anySecretMenuItemsAllowed() && (
            <FadeInSection
              styles={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant='h5'
                sx={{
                  width: '80%',
                  color: theme.palette.error.main,
                  margin: '32px 0 0 0',
                  padding: '32px 0',
                  borderTop: `1px dashed ${theme.palette.text.primary}`,
                  borderBottom: `1px dashed ${theme.palette.text.primary}`,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                Secret Pages
              </Typography>
            </FadeInSection>
          )}
          {anySecretMenuItemsAllowed() && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '32px',
                height: '100%',
                width: '80%',
                margin: '32px 0 0 0',
              }}
            >
              {secretMenuItems.map((menuItem, index) => (
                <ContentGuard
                  key={menuItem.name}
                  contentType={globalConstants.CONTENT_GUARD_REACT_ELEMENT}
                  // a 'user' that has 'elevated' privileges
                  isAllowed={menuItem.userAccessAllowed}
                >
                  <FadeInSection delay={(index + 1) * 100}>
                    <Box
                      key={menuItem.name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Button
                        fullWidth
                        onClick={() => {
                          setMenuDrawerOpen(false);
                          menuItem.action();
                        }}
                      >
                        <IconButton>{menuItem?.icon}</IconButton>
                        <Typography
                          sx={{
                            fontSize: theme.typography.h4,
                          }}
                        >
                          {menuItem.name}
                        </Typography>
                      </Button>
                    </Box>
                  </FadeInSection>
                </ContentGuard>
              ))}
            </Box>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default Navigation;
